import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { formatPriceHT, getPrimaryFormationPrice, getSecondaryFormationPrice } from "../../lib/formationsDefaults";

const cardMotion = {
  hidden: { opacity: 0, y: 24 },
  show: (index) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: index * 0.08, ease: "easeOut" },
  }),
};

export const FormationsCatalogSection = ({ formations, onSelectFormation }) => {
  return (
    <section className="px-4 py-8 sm:px-6 lg:px-8 lg:py-12" data-testid="formations-catalog-section">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="formations-catalog-eyebrow">
            Nos formations
          </div>
          <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="formations-catalog-title">
            Des formats clairs, comparables et pensés pour la montée en compétence.
          </h2>
          <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg" data-testid="formations-catalog-description">
            Chaque carte présente le prix standard HT en priorité, puis les options tarifaires disponibles selon le format et le mode de financement.
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3" data-testid="formations-catalog-grid">
          {formations.map((formation, index) => {
            const primaryPrice = getPrimaryFormationPrice(formation);
            const secondaryPrice = getSecondaryFormationPrice(formation);

            return (
              <motion.div
                custom={index}
                initial="hidden"
                key={formation.id}
                viewport={{ once: true, amount: 0.2 }}
                whileInView="show"
                variants={cardMotion}
              >
                <Card
                  className="group h-full cursor-pointer overflow-hidden rounded-[2rem] border border-[#d9e7d5]/90 bg-white shadow-[0_28px_76px_-54px_rgba(15,118,110,0.28)] transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_38px_90px_-50px_rgba(15,118,110,0.35)]"
                  data-testid={`formation-card-${formation.id}`}
                  onClick={() => onSelectFormation(formation)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectFormation(formation);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="aspect-[16/10] overflow-hidden border-b border-[#e8f2ee] bg-[#f8fcf3]" data-testid={`formation-card-image-wrapper-${formation.id}`}>
                    <img
                      alt={formation.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      data-testid={`formation-card-image-${formation.id}`}
                      src={formation.imageUrl}
                    />
                  </div>

                  <CardContent className="flex h-full flex-col p-7 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]" data-testid={`formation-card-eyebrow-${formation.id}`}>
                          Formation Bimcubes
                        </div>
                        <h3 className="bim-display mt-3 text-[1.35rem] font-semibold leading-tight text-slate-950" data-testid={`formation-card-title-${formation.id}`}>
                          {formation.title}
                        </h3>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-600" data-testid={`formation-card-meta-${formation.id}`}>
                      <div className="inline-flex items-center gap-2 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-3 py-1.5" data-testid={`formation-card-duration-days-${formation.id}`}>
                        <CalendarDays className="h-4 w-4 text-[#0f766e]" />
                        {formation.durationDays} jours
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-[#d8ece4] bg-white px-3 py-1.5" data-testid={`formation-card-duration-hours-${formation.id}`}>
                        <Clock3 className="h-4 w-4 text-[#0f766e]" />
                        {formation.durationHours} heures
                      </div>
                    </div>

                    <div className="mt-7 rounded-[1.6rem] border border-slate-200 bg-slate-50/80 p-5" data-testid={`formation-card-primary-price-block-${formation.id}`}>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Prix principal</div>
                      <div className="mt-3 text-2xl font-black text-slate-950 sm:text-3xl" data-testid={`formation-card-primary-price-${formation.id}`}>
                        Prix standard : {formatPriceHT(primaryPrice)}
                      </div>
                      <div className="mt-3 inline-flex rounded-full bg-[#ecfdf5] px-3 py-1.5 text-xs font-medium text-[#0f766e]" data-testid={`formation-card-secondary-price-${formation.id}`}>
                        Tarif personnel disponible à partir de {formatPriceHT(secondaryPrice)}
                      </div>
                    </div>

                    <div className="mt-6 space-y-3 border-t border-[#e8f2ee] pt-6" data-testid={`formation-card-price-list-${formation.id}`}>
                      {formation.prices.map((price) => (
                        <div className="flex items-start justify-between gap-4 text-sm" data-testid={`formation-card-price-row-${formation.id}-${price.id}`} key={price.id}>
                          <span className="max-w-[70%] text-slate-600">{price.label}</span>
                          <span className="font-semibold text-slate-950">{formatPriceHT(price.amount)}</span>
                        </div>
                      ))}
                    </div>

                    <Button className="mt-8 h-12 rounded-full bg-[#0f172a] text-white hover:bg-slate-800" data-testid={`formation-card-open-program-button-${formation.id}`} onClick={(event) => { event.stopPropagation(); onSelectFormation(formation); }} type="button">
                      Voir le programme
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};